import React, { useState, useEffect } from 'react';

interface NavbarProps {
    onOpenModal: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenModal }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [activeSection, setActiveSection] = useState('hero');

    useEffect(() => {
        let ticking = false;
        
        const updateScrollProgress = () => {
            const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
            const currentProgress = (window.scrollY / totalScroll) * 100;
            setScrollProgress(currentProgress);
            
            // Check which section is in view
            const sections = ['hero', 'features', 'how-it-works', 'roadmap', 'faq'];
            const scrollPosition = window.scrollY + 100;

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
            
            ticking = false;
        };
        
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(updateScrollProgress);
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/5">
            {/* Scroll Progress Bar */}
            <div className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 transition-none" style={{ width: `${scrollProgress}%`, willChange: 'width' }} />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <div className="flex items-center">
                        <img src="/logo.jpg" alt="NyxBot" className="w-8 h-8 rounded-full mr-2" />
                        <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                            NyxBot
                        </span>
                    </div>
                    
                    <div className="hidden md:block">
                        <div className="flex items-center space-x-8">
                            <a 
                                href="#features" 
                                className={`transition-colors ${activeSection === 'features' ? 'text-purple-400' : 'text-gray-300 hover:text-white'}`}
                            >
                                Features
                            </a>
                            <a 
                                href="#how-it-works" 
                                className={`transition-colors ${activeSection === 'how-it-works' ? 'text-purple-400' : 'text-gray-300 hover:text-white'}`}
                            >
                                How it Works
                            </a>
                            <a 
                                href="#roadmap" 
                                className={`transition-colors ${activeSection === 'roadmap' ? 'text-purple-400' : 'text-gray-300 hover:text-white'}`}
                            >
                                Roadmap
                            </a>
                            <a 
                                href="#faq" 
                                className={`transition-colors ${activeSection === 'faq' ? 'text-purple-400' : 'text-gray-300 hover:text-white'}`}
                            >
                                FAQ
                            </a>
                            <a 
                                href="https://x.com/nyktos_bot" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-gray-300 hover:text-white transition-colors"
                                aria-label="Twitter"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                            </a>
                            <button 
                                onClick={onOpenModal}
                                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-white font-semibold hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105"
                            >
                                Join Presale
                            </button>
                        </div>
                    </div>
                    
                    <div className="md:hidden">
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="text-gray-300 hover:text-white"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                {mobileMenuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            
            {mobileMenuOpen && (
                <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5">
                    <div className="px-2 pt-2 pb-3 space-y-1">
                        <a 
                            href="#features" 
                            className={`block px-3 py-2 ${activeSection === 'features' ? 'text-purple-400' : 'text-gray-300 hover:text-white'}`}
                        >
                            Features
                        </a>
                        <a 
                            href="#how-it-works" 
                            className={`block px-3 py-2 ${activeSection === 'how-it-works' ? 'text-purple-400' : 'text-gray-300 hover:text-white'}`}
                        >
                            How it Works
                        </a>
                        <a 
                            href="#roadmap" 
                            className={`block px-3 py-2 ${activeSection === 'roadmap' ? 'text-purple-400' : 'text-gray-300 hover:text-white'}`}
                        >
                            Roadmap
                        </a>
                        <a 
                            href="#faq" 
                            className={`block px-3 py-2 ${activeSection === 'faq' ? 'text-purple-400' : 'text-gray-300 hover:text-white'}`}
                        >
                            FAQ
                        </a>
                        <a 
                            href="https://x.com/nyktos_bot"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-3 py-2 text-gray-300 hover:text-white"
                        >
                            Twitter/X
                        </a>
                        <button 
                            onClick={onOpenModal}
                            className="w-full text-left px-3 py-2 text-purple-400 font-semibold"
                        >
                            Join Presale
                        </button>
                    </div>
                </div>
            )}
        </nav>
    );
};